// ═══════════════════════════════════════════════════════════════════════════
// ACHIEVEMENT NOTIFICATIONS - POLISH PACKAGE
// Celebrate user progress with milestone toasts
// ═══════════════════════════════════════════════════════════════════════════

// Purification milestones
export const PURIFICATION_MILESTONES = [
  { count: 10, message: '🎯 First 10 Purifications!', detail: 'You\'re getting the hang of this!' },
  { count: 25, message: '🔥 25 Purifications!', detail: 'Keep up the momentum!' },
  { count: 50, message: '⚡ 50 Purifications!', detail: 'You\'re on fire!' },
  { count: 100, message: '💯 100 Purifications!', detail: 'First major milestone!' },
  { count: 250, message: '🚀 250 Purifications!', detail: 'Cleanup machine!' },
  { count: 500, message: '💎 500 Purifications!', detail: 'You\'re a pro!' },
  { count: 1000, message: '👑 1,000 Purifications!', detail: 'LEGENDARY status!' },
  { count: 2500, message: '🏆 2,500 Purifications!', detail: 'Top tier purifier!' },
  { count: 5000, message: '⭐ 5,000 Purifications!', detail: 'Elite achievement!' },
  { count: 10000, message: '💫 10,000 Purifications!', detail: 'Absolute LEGEND!' },
];

// Objects cleaned milestones
export const OBJECTS_MILESTONES = [
  { count: 50, message: '🗑️ 50 Objects Cleaned!', detail: 'Making Sui cleaner!' },
  { count: 100, message: '🗑️ 100 Objects Cleaned!', detail: 'Wallet looking fresh!' },
  { count: 500, message: '🗑️ 500 Objects Cleaned!', detail: 'Cleanup champion!' },
  { count: 1000, message: '🗑️ 1,000 Objects Cleaned!', detail: 'So much cleaner!' },
  { count: 5000, message: '🗑️ 5,000 Objects Cleaned!', detail: 'Sanitation hero!' },
  { count: 10000, message: '🗑️ 10,000 Objects Cleaned!', detail: 'Unstoppable force!' },
];

// Burn milestones
export const BURN_MILESTONES = [
  { amount: 100, message: '🔥 100 $DISTIL Burned!', detail: 'Deflationary magic!' },
  { amount: 500, message: '🔥 500 $DISTIL Burned!', detail: 'Supply shrinking!' },
  { amount: 1000, message: '🔥 1,000 $DISTIL Burned!', detail: 'Making tokens scarce!' },
  { amount: 5000, message: '🔥 5,000 $DISTIL Burned!', detail: 'Massive burn!' },
  { amount: 10000, message: '🔥 10,000 $DISTIL Burned!', detail: 'Protocol burn king!' },
  { amount: 50000, message: '🔥 50,000 $DISTIL Burned!', detail: 'Legendary burns!' },
];

// Purity score milestones
export const PURITY_MILESTONES = [
  { score: 1000, message: '✨ 1,000 Purity Score!', detail: 'Building momentum!' },
  { score: 5000, message: '✨ 5,000 Purity Score!', detail: 'Nice progress!' },
  { score: 10000, message: '✨ 10,000 Purity Score!', detail: 'Tier climbing!' },
  { score: 50000, message: '✨ 50,000 Purity Score!', detail: 'Elite purifier!' },
  { score: 100000, message: '✨ 100,000 Purity Score!', detail: 'Legendary purity!' },
  { score: 500000, message: '✨ 500,000 Purity Score!', detail: 'Top tier achieved!' },
  { score: 1000000, message: '✨ 1,000,000 Purity Score!', detail: 'MAXIMUM PURITY!' },
];

// Streak milestones
export const STREAK_MILESTONES = [
  { days: 3, message: '🔥 3-Day Streak!', detail: 'Consistency pays off!' },
  { days: 7, message: '🔥 7-Day Streak!', detail: 'Weekly warrior!' },
  { days: 14, message: '🔥 14-Day Streak!', detail: 'Dedication level MAX!' },
  { days: 30, message: '🔥 30-Day Streak!', detail: 'Monthly mastery!' },
  { days: 60, message: '🔥 60-Day Streak!', detail: 'Unstoppable habit!' },
  { days: 100, message: '🔥 100-Day Streak!', detail: 'LEGENDARY dedication!' },
];

/**
 * Check if count/amount hits a milestone
 */
export function checkMilestone(value, milestones) {
  return milestones.find(m => {
    const key = m.count || m.amount || m.score || m.days;
    return value === key;
  });
}

/**
 * Check purification milestone
 */
export function checkPurificationMilestone(count) {
  return checkMilestone(count, PURIFICATION_MILESTONES);
}

/**
 * Check objects cleaned milestone
 */
export function checkObjectsMilestone(count) {
  return checkMilestone(count, OBJECTS_MILESTONES);
}

/**
 * Check burn milestone
 */
export function checkBurnMilestone(totalBurned) {
  return checkMilestone(Math.floor(totalBurned), BURN_MILESTONES);
}

/**
 * Check purity score milestone
 */
export function checkPurityMilestone(score) {
  return checkMilestone(Math.floor(score), PURITY_MILESTONES);
}

/**
 * Check streak milestone
 */
export function checkStreakMilestone(days) {
  return checkMilestone(days, STREAK_MILESTONES);
}

/**
 * Format milestone toast message
 */
export function formatMilestoneToast(milestone) {
  if (!milestone) return null;
  
  return {
    message: milestone.message,
    detail: milestone.detail,
    type: 'achievement'
  };
}

/**
 * Check holding tier milestone
 */
export function checkHoldingTierUnlock(oldBalance, newBalance, tier) {
  // Check if we just crossed a tier threshold
  const tiers = [
    { threshold: 1000, name: 'Bronze', emoji: '🥉', bonus: 5 },
    { threshold: 10000, name: 'Silver', emoji: '🥈', bonus: 10 },
    { threshold: 100000, name: 'Gold', emoji: '🏆', bonus: 15 },
    { threshold: 1000000, name: 'Diamond', emoji: '💎', bonus: 20 },
  ];
  
  for (const t of tiers) {
    if (oldBalance < t.threshold && newBalance >= t.threshold) {
      return {
        message: `${t.emoji} ${t.name} Tier Unlocked!`,
        detail: `+${t.bonus}% rewards on every purge!`
      };
    }
  }
  
  return null;
}

/**
 * Get all milestones user has achieved
 */
export function getAllAchievedMilestones(stats) {
  const achieved = [];
  
  // Purification milestones
  PURIFICATION_MILESTONES.forEach(m => {
    if (stats.totalPurifications >= m.count) {
      achieved.push({ ...m, type: 'purification', achieved: true });
    }
  });
  
  // Objects milestones
  OBJECTS_MILESTONES.forEach(m => {
    if (stats.totalObjectsCleaned >= m.count) {
      achieved.push({ ...m, type: 'objects', achieved: true });
    }
  });
  
  // Burn milestones
  BURN_MILESTONES.forEach(m => {
    if (stats.totalBurned >= m.amount) {
      achieved.push({ ...m, type: 'burn', achieved: true });
    }
  });
  
  // Purity milestones
  PURITY_MILESTONES.forEach(m => {
    if (stats.totalPurityEarned >= m.score) {
      achieved.push({ ...m, type: 'purity', achieved: true });
    }
  });
  
  return achieved;
}

// Export all
export default {
  PURIFICATION_MILESTONES,
  OBJECTS_MILESTONES,
  BURN_MILESTONES,
  PURITY_MILESTONES,
  STREAK_MILESTONES,
  checkPurificationMilestone,
  checkObjectsMilestone,
  checkBurnMilestone,
  checkPurityMilestone,
  checkStreakMilestone,
  checkHoldingTierUnlock,
  formatMilestoneToast,
  getAllAchievedMilestones,
};
